import { useLocation } from 'react-router-dom';

import { ActionButton } from 'components/shared/Button/Button';
import Checkbox from 'components/shared/ui/CheckBox';
import { useAuth } from 'context/AuthProvider';
import { useNavigation } from 'hooks/useNavigation';

import { ColumnFilters } from './ColumnFilters';
import { ExcelExport } from './ExcelExport';
import { TableNavWrapper, ActionsWrapper, MobileTopRow } from './TableNav.style';
import { getAllRows, getValidRows, getIsAllChecked } from '../Approval/apprvoval.utils';
import { SubmitApproval } from '../Approval/SubmitApprvoval';

type Props = {
  enable: boolean;
  length: number;
  columns: any[];
  excelData: any[];
  excelColumns: any[];
  tableRows?: any[];
  setSortBy?: (rules: any[]) => void;
  sortBy?: any[];
  approvalState: {
    item: any;
    data: any;
    onReset: () => void;
    onChange: (cell: any) => void;
    onMultiChange: (rows: any[]) => void;
  };
};

// New Button Component for creating new items
function NewButton() {
  const authState = useAuth();
  const location = useLocation();

  // Extract current object path from URL
  const currentPath = location.pathname.split('/')[1]; // e.g., 'customer' from '/customer/search/...'

  // Find the current object from auth state context
  const { objects = [] } = authState.context;
  const currentObject = objects.find((obj: any) => obj.OBJECTPATH === currentPath);

  // Find the CREATE option
  const createOption = currentObject?.MENUOPTIONS?.find(
    (option: any) => option.TYPE === 'CREATE',
  );

  if (!createOption) {
    return null;
  }

  const navigate = useNavigation(currentObject?.OBJECTPATH);

  const handleNewClick = () => {
    navigate(createOption);
  };
  return (
    <ActionButton
      data-cy='new-item'
      size='sm'
      onClick={handleNewClick}
      className='new-button'
    >
      + New
    </ActionButton>
  );
}

export default function TableNav({
  enable,
  length,
  columns,
  excelData,
  excelColumns,
  tableRows,
  setSortBy,
  sortBy,
  approvalState,
}: Props) {
  if (!enable) {
    return null;
  }

  // Check if we should show all buttons in one row on mobile
  // This happens when there are only Excel and View buttons (no approval items)
  const hasOnlyTwoButtons = !approvalState.item;

  // Check if we have exactly 4 items for full-width spacing
  const hasFourItems = approvalState.item && tableRows;

  return (
    <TableNavWrapper>
      <MobileTopRow $hasOnlyTwoButtons={hasOnlyTwoButtons}>
        <span id='records-count-mobile'>Total records {length}</span>
        <div className='mobile-actions'>
          <NewButton />
          {hasOnlyTwoButtons && (
            <>
              <ExcelExport columns={excelColumns} data={excelData} />
              <ColumnFilters
                columns={columns}
                length={length}
                setSortBy={setSortBy}
                sortBy={sortBy}
              />
            </>
          )}
        </div>
      </MobileTopRow>
      <span id='records-count'>Total records {length}</span>
      <ActionsWrapper
        className={`${approvalState.item ? 'approval-checkbox' : ''} ${hasOnlyTwoButtons ? 'mobile-two-buttons' : ''} ${hasFourItems ? 'four-items' : ''}`}
      >
        <NewButton />
        {(() => {
          if (!approvalState.item) return null;
          return (
            <SubmitApproval
              columns={columns}
              approvalItem={approvalState.item}
              approvalData={approvalState.data}
              onApprovalReset={approvalState.onReset}
              onApprovalChange={(cell) => approvalState.onChange(cell)}
            />
          );
        })()}
        <div className='excel-export'>
          <ExcelExport columns={excelColumns} data={excelData} />
        </div>
        <div className='column-filters'>
          <ColumnFilters
            columns={columns}
            length={length}
            setSortBy={setSortBy}
            sortBy={sortBy}
          />
        </div>
        {(() => {
          if (!tableRows) return null;
          if (!approvalState.item) return null;
          const allRows = getAllRows(tableRows);
          const validRows = getValidRows(tableRows);
          const isChecked = getIsAllChecked(validRows, approvalState.data);
          return (
            <div className='select-all-checkbox'>
              Select All
              <Checkbox
                data-cy='select-all-checkbox'
                isGeneral={true}
                checked={isChecked}
                /* eslint-disable-next-line @typescript-eslint/ban-ts-comment */
                // @ts-ignore
                onClick={() => approvalState.onMultiChange(allRows)}
              />
            </div>
          );
        })()}
      </ActionsWrapper>
    </TableNavWrapper>
  );
}
